import React, { useCallback, useEffect, useMemo } from 'react';
import { StyleSheet, Pressable } from 'react-native';
import { Image } from 'expo-image';
import { Link } from 'expo-router';
import { useToolStore, useUIStore } from '@/store';
import { useCurrentRoute } from '@/hooks/useCurrentRoute';
import useTranslation from '@/hooks/useTranslation';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import SidebarListItemLayout from './SidebarListItemLayout';

const FEATURED_LIMIT = 5;

const styles = StyleSheet.create({
  toolImage: {
    width: 16,
    height: 16,
    borderRadius: 4,
    backgroundColor: '#fff',
  },
});

const ToolIcon = ({ tool }) => {
  if (tool.avatarPath) {
    return (
      <Image
        source={{ uri: tool.avatarPath }}
        style={styles.toolImage}
        accessibilityLabel={`${tool.name} icon`}
        contentFit="cover"
      />
    );
  }
  return null;
};

// Logged-out-only nav entry: a collapsible "Tools" row that reveals a short
// list of featured tools plus a "See all tools" link, sourced from the public
// tools catalog.
function SidebarToolsNavSection() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const route = useCurrentRoute();
  const collapsedSections = useUIStore(state => state.collapsedSections);
  const toggleSection = useUIStore(state => state.toggleSection);
  const featuredSlugs = useToolStore(state => state.featuredSlugs);
  const toolsBySlug = useToolStore(state => state.toolsBySlug);
  const fetchFeatured = useToolStore(state => state.fetchFeatured);
  const isFetchedFeatured = useToolStore(state => state.isFetchedFeatured);
  const isLoadingFeatured = useToolStore(state => state.isLoadingFeatured);

  const isExpanded = !collapsedSections.sidebarTools;

  useEffect(() => {
    if (isExpanded && !isFetchedFeatured && !isLoadingFeatured) {
      try {
        fetchFeatured?.(8)?.catch?.(() => {});
      } catch (e) {}
    }
  }, [isExpanded, isFetchedFeatured, isLoadingFeatured, fetchFeatured]);

  // Only the chevron toggles the section — the row itself navigates to /tools.
  const handleChevronPress = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    toggleSection('sidebarTools');
  }, [toggleSection]);

  const featuredTools = useMemo(
    () => (featuredSlugs || []).map(slug => toolsBySlug[slug]).filter(Boolean).slice(0, FEATURED_LIMIT),
    [featuredSlugs, toolsBySlug],
  );

  const chevron = useMemo(() => (
    <Pressable
      onPress={handleChevronPress}
      hitSlop={8}
      accessibilityRole="button"
      accessibilityLabel={isExpanded ? t('sidebar.collapseTools') : t('sidebar.expandTools')}
      style={{ paddingHorizontal: 4, paddingVertical: 4, borderRadius: 4 }}
    >
      <Icon
        name={isExpanded ? 'chevron-down' : 'chevron-right'}
        size={12}
        color={colors.text_color_secondary}
      />
    </Pressable>
  ), [isExpanded, colors.text_color_secondary, handleChevronPress, t]);

  return (
    <>
      <Link href="/tools" asChild withAnchor={true}>
        <SidebarListItemLayout
          id="tools"
          title={t('navigation.tools') || 'Tools'}
          icon="wand-magic-sparkles"
          isActive={route.isTools}
          rightIcon={chevron}
          isRightContainerVisible
          noEllipsis
        />
      </Link>

      {isExpanded && (
        <>
          {featuredTools.map(tool => (
            <Link key={`featured-tool-${tool.slug}`} href={`/tools/${tool.slug}`} asChild withAnchor={true}>
              <SidebarListItemLayout
                id={`tool-${tool.slug}`}
                title={tool.name}
                icon={tool.avatarPath ? undefined : (tool.icon || 'wand-magic-sparkles')}
                customIcon={tool.avatarPath ? <ToolIcon tool={tool} /> : undefined}
                noEllipsis
                indentation={1}
              />
            </Link>
          ))}

          <Link href="/tools" asChild withAnchor={true}>
            <SidebarListItemLayout
              id="see-all-tools"
              title={t('sidebar.seeAllTools')}
              icon="arrow-right"
              noEllipsis
              indentation={1}
            />
          </Link>
        </>
      )}
    </>
  );
}

export default React.memo(SidebarToolsNavSection);
